import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { money, formatApiError } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { useFeedbackGate } from "@/components/FeedbackGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, Download, Printer, Trash2, Search, X } from "lucide-react";

const PAGE_SIZE = 10;
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const periodoSueldo = (x) => `${MESES[x.mes - 1]} ${x.anio}${x.quincena === "1" ? " — 1ª quinc." : x.quincena === "2" ? " — 2ª quinc." : ""}`;
const norm = (s) => (s == null ? "" : String(s)).toLowerCase();
const matches = (q, ...fields) => fields.some((f) => norm(f).includes(q));

const Pager = ({ page, total, setPage, testid }) => {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between px-3 py-2 border-t border-border text-xs text-slate-600" data-testid={testid}>
      <span data-testid={`${testid}-range`}>Mostrando {from}–{to} de {total}</span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-none h-7 px-2" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}>Anterior</Button>
        <span data-testid={`${testid}-indicator`}>Página {page} de {pages}</span>
        <Button variant="outline" size="sm" className="rounded-none h-7 px-2" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}>Siguiente</Button>
      </div>
    </div>
  );
};

const TipoBadge = ({ tipo }) => (
  <span className={`ml-2 inline-block text-[10px] px-1.5 py-0.5 border align-middle ${tipo === "sueldo" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
    {tipo === "sueldo" ? "Sueldo" : "Aguinaldo"}
  </span>
);

export default function ReceiptsPage() {
  const [agItems, setAgItems] = useState([]);
  const [suItems, setSuItems] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { guard } = useFeedbackGate();

  const loadAg = () => api.get("/receipts").then((r) => setAgItems(r.data)).catch(() => {});
  const loadSu = () => api.get("/sueldos/liquidaciones").then((r) => setSuItems(r.data.filter((x) => x.reciboGenerado))).catch(() => setSuItems([]));
  useEffect(() => { loadAg(); loadSu(); }, []);

  const rows = useMemo(() => {
    const ag = agItems.map((r) => ({
      tipo: "aguinaldo", key: `ag-${r.id}`, id: r.id,
      legajoNumero: r.empleado?.legajoNumero, nombre: r.empleado?.nombre, cuil: r.empleado?.cuil,
      periodo: r.periodoSemestre || r.periodo, neto: r.resultado?.neto,
      downloaded: r.counts?.downloaded || 0, printed: r.counts?.printed || 0, fecha: r.created_at,
    }));
    const su = (suItems || []).map((r) => ({
      tipo: "sueldo", key: `su-${r.legajoId}`, legajoId: r.legajoId,
      legajoNumero: r.legajoNumero, nombre: r.legajoNombre, cuil: r.cuil,
      periodo: periodoSueldo(r), neto: r.neto,
      downloaded: r.counts?.downloaded || 0, printed: r.counts?.printed || 0, fecha: r.updatedAt,
    }));
    return [...ag, ...su].sort((a, b) => String(b.fecha || "").localeCompare(String(a.fecha || "")));
  }, [agItems, suItems]);

  const query = norm(q).trim();
  const filtered = useMemo(() => (query ? rows.filter((r) => matches(query, r.legajoNumero, r.nombre, r.cuil)) : rows), [rows, query]);
  useEffect(() => { setPage(1); }, [query]);
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openPdf = async (r, action) => {
    const url = r.tipo === "aguinaldo" ? `/receipts/${r.id}/pdf?action=${action}` : `/sueldos/liquidaciones/${r.legajoId}/pdf?action=${action}`;
    const { data } = await api.get(url, { responseType: "blob" });
    return URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  };
  const reload = (r) => (r.tipo === "aguinaldo" ? loadAg() : loadSu());
  const view = async (r) => {
    try { await guard(async () => window.open(await openPdf(r, "view"), "_blank")); } catch { toast.error("No se pudo abrir el PDF"); }
  };
  const download = async (r) => {
    try {
      await guard(async () => {
        const url = await openPdf(r, "downloaded");
        const a = document.createElement("a");
        a.href = url; a.download = r.tipo === "aguinaldo" ? `recibo_${r.id}.pdf` : `recibo_sueldo_${r.legajoId}.pdf`; a.click();
        toast.success("Recibo descargado"); reload(r);
      });
    } catch { toast.error("Error al descargar"); }
  };
  const print = async (r) => {
    try {
      await guard(async () => {
        const url = await openPdf(r, "printed");
        const w = window.open(url, "_blank");
        if (w) w.addEventListener("load", () => w.print());
        toast.success("Enviado a impresión"); reload(r);
      });
    } catch { toast.error("Error al imprimir"); }
  };
  const remove = async (r) => {
    if (!window.confirm("¿Eliminar este recibo? El cálculo asociado quedará editable nuevamente (Borrador).")) return;
    try {
      if (r.tipo === "aguinaldo") await api.delete(`/receipts/${r.id}`);
      else await api.delete(`/sueldos/liquidaciones/${r.legajoId}/recibo`);
      toast.success("Recibo eliminado"); reload(r);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AppLayout title="Recibos" subtitle="Recibos Ley 20.744 emitidos — Aguinaldo y Sueldos">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white border border-border p-4">
          <div className="relative max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input type="text" placeholder="Buscar por N° de legajo, nombre o CUIL…" value={q} onChange={(e) => setQ(e.target.value)}
              className="rounded-none pl-9 pr-8 h-9" data-testid="recibos-search" />
            {q && (
              <button type="button" onClick={() => setQ("")} data-testid="recibos-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={14} /></button>
            )}
          </div>
        </div>

        <div className="bg-white border border-border" data-testid="recibos-unificados">
          <div className="px-5 py-3 brand-bg"><span className="label-tiny label-on-dark">Recibos emitidos</span></div>
          {suItems === null ? (
            <p className="p-8 text-sm text-muted-foreground">Cargando…</p>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="recibos-empty">
              {query ? <>Sin resultados para «{q}».</> : <>No hay recibos emitidos. Generá uno desde las calculadoras de Aguinaldo o Recibo de Sueldos.</>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="recibos-table">
                <thead><tr className="border-b border-border text-left">
                  {["Legajo N°", "Apellido y Nombre", "CUIL", "Período", "Neto", "Descargas", "Impresiones", ""].map((h) => <th key={h} className="p-3 label-tiny">{h}</th>)}
                </tr></thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.key} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid={r.tipo === "aguinaldo" ? `recibo-${r.id}` : `recibo-sueldo-${r.legajoId}`}>
                      <td className="p-3 font-mono text-xs">{r.legajoNumero || "—"}</td>
                      <td className="p-3 font-medium text-slate-900">{r.nombre || "—"}</td>
                      <td className="p-3 font-mono text-xs">{r.cuil || "—"}</td>
                      <td className="p-3 whitespace-nowrap">{r.periodo || "—"}<TipoBadge tipo={r.tipo} /></td>
                      <td className="p-3 tabular">{r.neto != null ? money(r.neto) : "—"}</td>
                      <td className="p-3 tabular">{r.downloaded}</td>
                      <td className="p-3 tabular">{r.printed}</td>
                      <td className="p-3">
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" title="Ver" data-testid={r.tipo === "aguinaldo" ? `view-${r.id}` : `view-sueldo-${r.legajoId}`} className="rounded-none h-8 w-8" onClick={() => view(r)}><Eye size={15} /></Button>
                          <Button size="icon" variant="ghost" title="Descargar" data-testid={r.tipo === "aguinaldo" ? `download-${r.id}` : `download-sueldo-${r.legajoId}`} className="rounded-none h-8 w-8" onClick={() => download(r)}><Download size={15} /></Button>
                          <Button size="icon" variant="ghost" title="Imprimir" data-testid={r.tipo === "aguinaldo" ? `print-${r.id}` : `print-sueldo-${r.legajoId}`} className="rounded-none h-8 w-8" onClick={() => print(r)}><Printer size={15} /></Button>
                          <Button size="icon" variant="ghost" title="Eliminar" data-testid={r.tipo === "aguinaldo" ? `del-receipt-${r.id}` : `del-sueldo-recibo-${r.legajoId}`} className="rounded-none h-8 w-8 text-destructive" onClick={() => remove(r)}><Trash2 size={15} /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={page} total={filtered.length} setPage={setPage} testid="recibos-pagination" />
        </div>
      </div>
    </AppLayout>
  );
}
